import React, { useMemo } from "react";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useTranslation } from "react-i18next";
import { RequestDetailBoxLayout } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestDetailBox/RequestDetailBoxLayout";
import { RequestSummary } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestSummary";
import { RequestRecommendations } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestRecommendations";
import { RequestPastRecommendations } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestPastRecommendations/RequestPastRecommendations";
import { RequestPastProposals } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestPastProposals/RequestPastProposals";
import { RequestDetailBoxProps } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestDetailBox/requestDetailBox.model";
import { RequestDetailContent } from "@companion-core/shared/app/Components/Requests/RequestDetail/requestDetail.model";
import { createSortedSummaryFields } from "@companion-core/shared/app/Utils/myRequests";

dayjs.extend(localizedFormat);

export const RequestDetailBox = ({
  requestData,
  proposalsData,
  isProposalsPending,
  requestDetailContent,
  performCancelRequest,
  performUpdateRequest,
  performDisplayProposals,
  performDisplayRecommendations,
  performDisplayRecommendationDetail,
  goBack,
  contentId,
}: RequestDetailBoxProps) => {
  const { t, i18n } = useTranslation();
  const summary = createSortedSummaryFields(requestData, t, i18n.language);
  const status = useMemo(
    () =>
      !requestData.has_digital_proposal && requestData.status === "recommended"
        ? "recommended-by-mail"
        : requestData.status,
    [requestData]
  );
  const listProposals = useMemo(
    () =>
      proposalsData?.data?.results?.filter((proposal) =>
        ["rejected", "validated"].includes(proposal.status)
      ),
    [proposalsData]
  );
  const proposalId = useMemo(() => {
    if (contentId) return contentId;
    return listProposals?.length === 1 ? listProposals[0].id : null;
  }, [contentId, proposalsData]);
  // The "displayListRecommendations" variable controls the redirection of the card and "see my proposals" button
  // Either redirect to listRecommendations or list proposals
  const displayListRecommendations = useMemo(
    () => listProposals?.length === 1 || status === "recommended",
    [status, proposalsData]
  );
  const proposalDate = useMemo(() => {
    if (!contentId) return;
    const matchingProposal = proposalsData?.data?.results?.find((value) => value.id === contentId);
    return matchingProposal ? dayjs(matchingProposal.generated_date).format("L") : undefined;
  }, [contentId, proposalsData]);
  const boxTitle = useMemo(() => {
    let titleKey = "";
    if (requestDetailContent !== RequestDetailContent.proposals) {
      titleKey = `request.my-requests.${requestDetailContent}.title`;
    }
    if (requestDetailContent === RequestDetailContent.proposals && contentId)
      return t("request.my-requests.proposals.past.title", {
        val: proposalDate,
        "interpolation": { "escapeValue": false },
      });
    switch (requestData.status) {
      case "proposal-validated":
        titleKey = `request.my-requests.${requestDetailContent}.validated.title`;
        break;
      case "proposal-rejected":
        titleKey = `request.my-requests.${requestDetailContent}.rejected.title`;
        break;
      default:
        titleKey = `request.my-requests.${requestDetailContent}.title`;
    }
    return t(titleKey);
  }, [requestDetailContent, contentId, proposalDate]);
  const detailsBoxBackAriaLabel = useMemo(
    () => t(`request.my-requests.details.${requestDetailContent}.back.accessibility-label`),
    [requestDetailContent, t]
  );

  return (
    <RequestDetailBoxLayout
      backAriaLabel={detailsBoxBackAriaLabel}
      title={boxTitle}
      content={requestDetailContent}
      status={status}
      requestId={requestData.id}
      family={requestData.family ?? ""}
      subFamily={requestData.sub_family ?? undefined}
      hasDigitalProposal={requestData.has_digital_proposal}
      proposalsData={proposalsData}
      isProposalsPending={isProposalsPending}
      performDisplayProposals={
        displayListRecommendations ? performDisplayRecommendations : performDisplayProposals
      }
      performDisplayRecommendationDetail={performDisplayRecommendationDetail}
      goBack={goBack}>
      {requestDetailContent === RequestDetailContent.recommendations ? (
        ["proposal-validated", "proposal-rejected"].includes(status) ? (
          <RequestPastRecommendations requestData={requestData} proposalsData={proposalsData} />
        ) : (
          <RequestRecommendations requestData={requestData} proposalsData={proposalsData} />
        )
      ) : requestDetailContent === RequestDetailContent.proposals ? (
        proposalId ? (
          <RequestPastRecommendations
            requestData={requestData}
            contentId={proposalId}
            proposalsData={proposalsData}
          />
        ) : (
          <RequestPastProposals requestData={requestData} />
        )
      ) : (
        <RequestSummary
          requestData={requestData}
          proposalsData={proposalsData}
          summary={summary}
          status={status}
          performCancelRequest={performCancelRequest}
          performUpdateRequest={performUpdateRequest}
          performDisplayProposals={
            displayListRecommendations ? performDisplayRecommendations : performDisplayProposals
          }
          performDisplayRecommendationDetail={performDisplayRecommendationDetail}
        />
      )}
    </RequestDetailBoxLayout>
  );
};
